import { useState, useCallback, useRef } from 'react'
import type { DeliveryAddress, ClientUser } from '@restaurant/core'
import type { DeliveryService } from '@restaurant/core'
import type { AlertAction } from '@restaurant/core'
import { resolveDeliveryFee, resolveRemainingFees, resolveRemainingFeesProgressively, haversineDistance, KM_PER_MILE } from '@restaurant/core'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Minimal Location shape needed by the hook (avoids tight coupling to the full Location model). */
export interface DeliveryGuardLocation {
  id: string
  name: string
  phone?: string
  latitude?: number
  longitude?: number
}

const MAX_DELIVERY_DISTANCE_MI = 15

interface CandidateFilterResult {
  candidates: DeliveryGuardLocation[]
  nonCandidates: DeliveryGuardLocation[]
}

export interface UseDeliveryGuardDeps {
  currentAddress: DeliveryAddress | null
  savedAddresses: DeliveryAddress[]
  currentLocation: DeliveryGuardLocation | null
  locations: DeliveryGuardLocation[]
  businessId: string | null
  orderType: 'Pickup' | 'Delivery'
  setOrderType: (type: 'Pickup' | 'Delivery') => void
  setCurrentAddress: (address: DeliveryAddress) => void
  updateAddressFees: (addressId: string, fees: Record<string, number>) => void
  setDeliveryFee: (fee: number) => void
  deliveryService: Pick<DeliveryService, 'batchFetchDeliveryFees' | 'updateStoreDeliveryFees' | 'getDeliveryQuote'>
  user: ClientUser | null
}

export interface DeliveryGuardAlert {
  visible: boolean
  title: string
  description: string
  primaryAction: AlertAction
  secondaryAction?: AlertAction
}

export interface DeliveryGuardReturn {
  alertProps: DeliveryGuardAlert
  isCheckingFees: boolean
  onAddressSelected: (address: DeliveryAddress, onSuccess: () => void, sheetControls?: { close: () => void, reopen: () => void }) => Promise<void>
  onNewAddressValidated: (address: DeliveryAddress, proceedToDetails: () => void, sheetControls?: { close: () => void, reopen: () => void }) => Promise<void>
  onDeliveryToggled: (openAddressSheet: () => void) => void
  checkHydratedAddress: () => void
  fetchRemainingFees: (address: DeliveryAddress) => Promise<void>
  fetchRemainingFeesProgressively: (address: DeliveryAddress, candidateLocations: DeliveryGuardLocation[]) => Promise<void>
  /** Get the pending address from onNewAddressValidated (before user confirms). Null if none pending. */
  getPendingAddress: () => DeliveryAddress | null
  getLocationDeliveryStatus: (locationId: string) => 'available' | 'out-of-range' | 'unchecked'
  getLocationDeliveryFee: (locationId: string) => number | null
  evaluateDeliveryCandidates: (address: DeliveryAddress, onChangeAddress?: () => void) => Promise<{ candidates: DeliveryGuardLocation[]; passed: boolean; isTransientError: boolean }>
}

// ---------------------------------------------------------------------------
// Pure helpers (exported for unit testing)
// ---------------------------------------------------------------------------

/**
 * Determine the delivery status for a given location based on cached fees.
 * - fee >= 0  -> 'available' (includes $0 free delivery)
 * - fee === -1 -> 'out-of-range'
 * - undefined  -> 'unchecked'
 */
export function getDeliveryStatus(
  fees: Record<string, number> | undefined,
  locationId: string
): 'available' | 'out-of-range' | 'unchecked' {
  if (!fees) return 'unchecked'
  const fee = fees[locationId]
  if (fee === undefined) return 'unchecked'
  if (fee === -1) return 'out-of-range'
  return 'available'
}

/**
 * Return the numeric delivery fee for a location, or null if unavailable.
 * Returns null for missing entries AND for -1 (out-of-range sentinel).
 */
export function getDeliveryFee(
  fees: Record<string, number> | undefined,
  locationId: string
): number | null {
  if (!fees) return null
  const fee = fees[locationId]
  if (fee === undefined || fee === -1) return null
  return fee
}

/**
 * Filter locations into candidates (within MAX_DELIVERY_DISTANCE_MI) and
 * non-candidates (beyond threshold), sorted closest-first.
 * If the address has no coordinates, all locations are returned as candidates.
 */
export function filterCandidates(
  locations: DeliveryGuardLocation[],
  address: { latitude?: number; longitude?: number }
): CandidateFilterResult {
  if (address.latitude === undefined || address.longitude === undefined) {
    return { candidates: [...locations], nonCandidates: [] }
  }

  const addrLat = address.latitude
  const addrLng = address.longitude

  const withDistances = locations.map((loc) => {
    if (loc.latitude === undefined || loc.longitude === undefined) {
      return { loc, distanceMi: Infinity }
    }
    const distanceKm = haversineDistance(addrLat, addrLng, loc.latitude, loc.longitude)
    const distanceMi = distanceKm / KM_PER_MILE
    return { loc, distanceMi }
  })

  withDistances.sort((a, b) => a.distanceMi - b.distanceMi)

  const candidates: DeliveryGuardLocation[] = []
  const nonCandidates: DeliveryGuardLocation[] = []

  for (const { loc, distanceMi } of withDistances) {
    if (distanceMi <= MAX_DELIVERY_DISTANCE_MI) {
      candidates.push(loc)
    } else {
      nonCandidates.push(loc)
    }
  }

  return { candidates, nonCandidates }
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useDeliveryGuard(deps: UseDeliveryGuardDeps): DeliveryGuardReturn {
  const {
    currentAddress,
    currentLocation,
    locations,
    businessId,
    orderType,
    setOrderType,
    setCurrentAddress,
    updateAddressFees,
    setDeliveryFee,
    deliveryService,
    user,
  } = deps

  // ---- internal state ----
  const [isCheckingFees, setIsCheckingFees] = useState(false)
  const [alertVisible, setAlertVisible] = useState(false)
  const [alertType, setAlertType] = useState<'too-far' | 'transient-error'>('too-far')

  // Track which address the in-flight async request was made for.
  const inflightAddressIdRef = useRef<string | null>(null)

  // Holds the validated address with fees from onNewAddressValidated (A1 flow).
  // Not committed to currentAddress until the user confirms via address details.
  const pendingAddressRef = useRef<DeliveryAddress | null>(null)

  // Callback to reopen address sheet when "Change Address" is pressed in alert
  const changeAddressCallbackRef = useRef<(() => void) | null>(null)

  // ---- derived ----
  const phoneNumber = currentLocation?.phone || user?.phoneNumber || ''

  // ---- alert construction ----
  const hideAlert = useCallback(() => setAlertVisible(false), [])

  const showTooFarAlert = useCallback(
    (onChangeAddress?: () => void) => {
      changeAddressCallbackRef.current = onChangeAddress || null
      setAlertType('too-far')
      setAlertVisible(true)
    },
    []
  )

  const showTransientErrorAlert = useCallback(
    (onTryAgain?: () => void) => {
      changeAddressCallbackRef.current = onTryAgain || null
      setAlertType('transient-error')
      setAlertVisible(true)
    },
    []
  )

  const alertProps: DeliveryGuardAlert = alertType === 'transient-error'
    ? {
        visible: alertVisible,
        title: 'Something Went Wrong',
        description: 'We could not check delivery availability. Please try again.',
        primaryAction: {
          text: 'Try Again',
          onPress: () => {
            const cb = changeAddressCallbackRef.current
            changeAddressCallbackRef.current = null
            hideAlert()
            cb?.()
          },
        },
        secondaryAction: {
          text: 'Switch to Pickup Order',
          onPress: () => {
            setOrderType('Pickup')
            setDeliveryFee(0)
            changeAddressCallbackRef.current = null
            hideAlert()
          },
        },
      }
    : {
        visible: alertVisible,
        title: 'Delivery Address Too Far',
        description: 'Delivery is not available to this address.',
        primaryAction: {
          text: 'Switch to Pickup Order',
          onPress: () => {
            setOrderType('Pickup')
            setDeliveryFee(0)
            changeAddressCallbackRef.current = null
            hideAlert()
          },
        },
        secondaryAction: {
          text: 'Change Delivery Address',
          onPress: () => {
            const cb = changeAddressCallbackRef.current
            changeAddressCallbackRef.current = null
            hideAlert()
            cb?.()
          },
        },
      }

  // ---- Phase 1: fetch fee for a target location ----

  /**
   * Fetch a delivery fee quote for a specific location (defaults to currentLocation).
   * Returns { fee, locationId } or null if the request was stale / failed.
   */
  const fetchSingleLocationFee = useCallback(
    async (address: DeliveryAddress, targetLoc?: DeliveryGuardLocation): Promise<{ fee: number; locationId: string } | null> => {
      const targetLocation = targetLoc ?? currentLocation
      if (!businessId || !targetLocation) return null

      inflightAddressIdRef.current = address.id
      setIsCheckingFees(true)

      try {
        const result = await resolveDeliveryFee(address, {
          deliveryService,
          businessId,
          currentLocation: targetLocation,
          locations,
          phoneNumber,
          updateAddressFees,
        })

        if (inflightAddressIdRef.current !== address.id) return null
        if (!result) return null

        return { fee: result.fee, locationId: targetLocation.id }
      } catch {
        return null
      } finally {
        setIsCheckingFees(false)
      }
    },
    [businessId, currentLocation, locations, phoneNumber, deliveryService, updateAddressFees]
  )

  // ---- Phase 2: fetch remaining locations in background ----

  /**
   * Fetch fees for all locations (skipping those already cached).
   * Intended to run in the background after the user confirms an address.
   */
  const fetchRemainingFees = useCallback(
    async (address: DeliveryAddress): Promise<void> => {
      if (!businessId) return

      await resolveRemainingFees(address, {
        deliveryService,
        businessId,
        currentLocation: currentLocation!,
        locations,
        phoneNumber,
        updateAddressFees,
      })
    },
    [businessId, currentLocation, locations, phoneNumber, deliveryService, updateAddressFees]
  )

  /**
   * Kick off progressive batch fetch for remaining candidate locations.
   * Calls updateAddressFees after each DoorDash response so the UI updates
   * progressively (each cell transitions from loading to enabled/disabled).
   */
  const fetchRemainingFeesProgressively = useCallback(
    async (address: DeliveryAddress, candidateLocations: DeliveryGuardLocation[]): Promise<void> => {
      if (!businessId) return

      await resolveRemainingFeesProgressively(
        address,
        candidateLocations,
        {
          deliveryService,
          businessId,
          currentLocation: currentLocation!,
          phoneNumber,
          updateAddressFees,
        }
      )
    },
    [businessId, currentLocation, phoneNumber, deliveryService, updateAddressFees]
  )

  // ---- Candidate evaluation ----

  /**
   * Filter locations by distance, write -1 for non-candidates, then probe the
   * closest one or two candidates to determine delivery availability.
   */
  const evaluateDeliveryCandidates = useCallback(
    async (address: DeliveryAddress, onChangeAddress?: () => void): Promise<{ candidates: DeliveryGuardLocation[]; passed: boolean; isTransientError: boolean }> => {
      const { candidates, nonCandidates } = filterCandidates(locations, address)

      // Write -1 sentinel for all non-candidates
      if (nonCandidates.length > 0) {
        const nonCandidateFees = nonCandidates.reduce<Record<string, number>>((acc, loc) => {
          acc[loc.id] = -1
          return acc
        }, {})
        updateAddressFees(address.id, nonCandidateFees)
      }

      if (candidates.length === 0) {
        showTooFarAlert(onChangeAddress)
        return { candidates: [], passed: false, isTransientError: false }
      }

      // Probe first (closest) candidate
      const firstResult = await fetchSingleLocationFee(address, candidates[0])

      if (firstResult === null) {
        // Stale request (user switched addresses) — silently ignore
        if (inflightAddressIdRef.current !== address.id) {
          return { candidates, passed: false, isTransientError: false }
        }
        // Transient error — could not reach service
        showTransientErrorAlert(onChangeAddress)
        return { candidates, passed: false, isTransientError: true }
      }

      if (firstResult.fee >= 0) {
        return { candidates, passed: true, isTransientError: false }
      }

      // First candidate returned -1 (out of range)
      if (candidates.length < 2) {
        showTooFarAlert(onChangeAddress)
        return { candidates, passed: false, isTransientError: false }
      }

      // Probe second candidate
      const secondResult = await fetchSingleLocationFee(address, candidates[1])

      if (secondResult === null) {
        if (inflightAddressIdRef.current !== address.id) {
          return { candidates, passed: false, isTransientError: false }
        }
        showTransientErrorAlert(onChangeAddress)
        return { candidates, passed: false, isTransientError: true }
      }

      if (secondResult.fee >= 0) {
        return { candidates, passed: true, isTransientError: false }
      }

      // Both candidates returned -1
      showTooFarAlert(onChangeAddress)
      return { candidates, passed: false, isTransientError: false }
    },
    [locations, updateAddressFees, fetchSingleLocationFee, showTooFarAlert, showTransientErrorAlert]
  )

  // ---- public methods ----

  /**
   * A2 — saved address selected from the address sheet.
   * Uses cached fee if available, otherwise fetches just the current location.
   * Kicks off background fetch for remaining locations.
   */
  const onAddressSelected = useCallback(
    async (address: DeliveryAddress, onSuccess: () => void, sheetControls?: { close: () => void, reopen: () => void }): Promise<void> => {
      const existingFee = address.storeDeliveryFees?.[currentLocation?.id ?? '']

      let fee: number
      let addressWithFees: DeliveryAddress

      if (existingFee !== undefined) {
        // Use cached fee
        fee = existingFee
        addressWithFees = address
      } else {
        // Fetch just the current location
        const result = await fetchSingleLocationFee(address)
        if (!result) {
          // Fee fetch failed — show alert with retry-friendly message
          showTooFarAlert()
          return
        }
        if (inflightAddressIdRef.current !== address.id) return

        fee = result.fee
        addressWithFees = { ...address, storeDeliveryFees: { ...address.storeDeliveryFees, [result.locationId]: fee } }
      }

      if (fee >= 0) {
        setOrderType('Delivery')
        setCurrentAddress(addressWithFees)
        setDeliveryFee(fee)
        onSuccess()

        // Kick off background fetch for remaining locations
        fetchRemainingFeesProgressively(addressWithFees, locations).catch(() => {})
      } else {
        // Close sheet first so alert appears on top, then show alert.
        // "Change Delivery Address" will reopen the sheet via the stored callback.
        sheetControls?.close()
        showTooFarAlert(sheetControls?.reopen)
      }
    },
    [fetchSingleLocationFee, fetchRemainingFeesProgressively, locations, currentLocation, setOrderType, setCurrentAddress, setDeliveryFee, showTooFarAlert]
  )

  /**
   * A1 — new autocomplete address validated (not yet saved).
   * Fetches fee for just the current location. Remaining locations
   * are fetched after the user confirms the address details.
   */
  const onNewAddressValidated = useCallback(
    async (address: DeliveryAddress, proceedToDetails: () => void, sheetControls?: { close: () => void, reopen: () => void }): Promise<void> => {
      const result = await fetchSingleLocationFee(address)
      if (!result) {
        sheetControls?.close()
        showTooFarAlert(sheetControls?.reopen)
        return
      }

      if (inflightAddressIdRef.current !== address.id) return

      const { fee, locationId } = result
      const addressWithFees = { ...address, storeDeliveryFees: { ...address.storeDeliveryFees, [locationId]: fee } }

      if (fee >= 0) {
        // Don't commit to currentAddress yet — store in ref until user confirms.
        // This prevents stale addresses persisting if the user abandons the flow.
        pendingAddressRef.current = addressWithFees
        setOrderType('Delivery')
        setDeliveryFee(fee)
        proceedToDetails()
      } else {
        pendingAddressRef.current = null
        sheetControls?.close()
        showTooFarAlert(sheetControls?.reopen)
      }
    },
    [fetchSingleLocationFee, currentLocation, setOrderType, setDeliveryFee, showTooFarAlert]
  )

  /**
   * Synchronous toggle — user taps the Delivery toggle.
   */
  const onDeliveryToggled = useCallback(
    (openAddressSheet: () => void): void => {
      openAddressSheet()
    },
    []
  )

  /**
   * Synchronous check — used on hydration to validate a persisted Delivery selection.
   */
  const checkHydratedAddress = useCallback((): void => {
    if (orderType !== 'Delivery') return

    if (!currentAddress || !currentLocation) return

    const fee = currentAddress.storeDeliveryFees?.[currentLocation.id]

    if (fee !== undefined && fee >= 0) {
      setDeliveryFee(fee)
    } else {
      showTooFarAlert()
    }
  }, [orderType, currentAddress, currentLocation, setDeliveryFee, showTooFarAlert])

  /**
   * Pure lookup — delivery status for a location.
   */
  const getLocationDeliveryStatus = useCallback(
    (locationId: string): 'available' | 'out-of-range' | 'unchecked' => {
      return getDeliveryStatus(currentAddress?.storeDeliveryFees, locationId)
    },
    [currentAddress]
  )

  /**
   * Pure lookup — delivery fee for a location (null if unknown or out-of-range).
   */
  const getLocationDeliveryFee = useCallback(
    (locationId: string): number | null => {
      return getDeliveryFee(currentAddress?.storeDeliveryFees, locationId)
    },
    [currentAddress]
  )

  const getPendingAddress = useCallback(() => pendingAddressRef.current, [])

  return {
    alertProps,
    isCheckingFees,
    onAddressSelected,
    onNewAddressValidated,
    onDeliveryToggled,
    checkHydratedAddress,
    fetchRemainingFees,
    fetchRemainingFeesProgressively,
    getPendingAddress,
    getLocationDeliveryStatus,
    getLocationDeliveryFee,
    evaluateDeliveryCandidates,
  }
}
